/* eslint-disable react/jsx-key */
import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'next-i18next';
import Container from './Container';
import Intro from './Intro';
import Dropzone from './Dropzone';
import PdfEditor from './PdfEditor';
import ActionsGrid from './ActionsGrid';
import ErrorModal from './ErrorModal';
import s, { ConvertResponseStruct } from '../utils/struct';
import { apiUrl } from '../utils/apiRequest';
import getErrorName from '../utils/getErrorName';
import useApiRequest from '../utils/useApiRequest';
import useUpload from '../utils/useUpload';
import usePdfAccept from '../utils/usePdfAccept';
import { sendEvent } from '../utils/analytics';
import useInterruptRouting from '../utils/useInterruptRouting';
import LargeLoader from './LargeLoader';
import useEditorStore from '../store/useEditorStore';
import FileIcon from "./FileIcon";
import IconDownload from '../public/images/download.svg'
import IconDelete from '../public/images/delete-2.svg'
import IconStartOver from '../public/images/start-over.svg'
import { saveAs } from 'file-saver';
import GridEditSign from "./gridElements/GridEditSign";
import GridConvertPdf from "./gridElements/GridConvertPdf";
import GridConvertToPdf from "./gridElements/GridConvertToPdf";
import GridEditPdf from "./gridElements/GridEditPdf";
import filesize from "filesize";

const ModalEditPdf = () => {
  const { step, setFile, setStep, reset } = useEditorStore();
  const [isStepReset, setStepReset] = useInterruptRouting(step);

  const handleGoToStep2 = useCallback(
    (file) => {
      setStepReset(false);
      setFile(file);
      setStep(2);
    },
    [setStepReset, setFile, setStep]);

  useEffect(() => {
    if (step) {
      window.scrollTo(0, 0);
    }
    isStepReset && reset();
  }, [step, isStepReset, reset]);

  return [<Step1 onNextStep={handleGoToStep2} />, <Step2 />, <Step3/>].at(step - 1);
};

const Step1 = ({ onNextStep }) => {
  const [files, setFiles] = useState([]);
  const store = useEditorStore()

  const { processing, fileWithError, handleAccepted, errorModal } =
    usePdfAccept(files, setFiles, false);

  const hasFileWithError = !!fileWithError;

  useEffect(() => {
    if (hasFileWithError || !files.length) {
      return;
    }

    onNextStep(files[0]);
  }, [files, hasFileWithError, onNextStep]);

  useEffect(() => {
    store.reset()
  }, []);

  return (
    <>
      <Container className=" card-edit-pdf__index">
        <Dropzone
          type="pdf"
          disabled={processing || hasFileWithError}
          onAccepted={handleAccepted}
        />
      </Container>
      {errorModal}
    </>
  );
};

const Step2 = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'edit-pdf']);
  const { upload, uploading, error } = useUpload();
  const zoom = 2;

  const { sendApiRequest } = useApiRequest({ url: '/api/pdf/to/html' });
  const store = useEditorStore();
  const { file, handleGoToStep3, isUploadError, setIsUploadError, setUploadFileResponce } = useEditorStore();

  useEffect(() => {
    upload([file?.file], (uploadedFiles) => {
      const uploadedFile = uploadedFiles[0];
      setUploadFileResponce(uploadedFile)

      sendApiRequest({
        data: {
          name: uploadedFile.name,
          filename: uploadedFile.hash,
          zoom,
        },
        validateResponse(response) {
          s.assert(response.data, ConvertResponseStruct);
        },
        onSuccess(response, time) {
          store.setReady(true);
          sendEvent({
            category: 'pdf-to-html',
            action: 'success',
            label: `${time}`,
          });
        },
        onError(error, time) {
          sendEvent({
            category: 'pdf-to-html',
            action: 'error',
            label: `${getErrorName(error)}:${time}`,
          });
          setIsUploadError(true);
        },
      });
    });
  }, [file, upload, sendApiRequest, setUploadFileResponce, setIsUploadError]);

  const handleUploadErrorModalHide = useCallback(() => {
    setIsUploadError(false);
  }, []);

  const handleSave = useCallback(
    ({ url, size }) => {
      handleGoToStep3({
        fileName: file.file.name,
        fileUrl: apiUrl(url),
        fileSize: size,
      });
    },
    [handleGoToStep3, file]);
  let errorModal;

  if (error) {
    errorModal = (
      <ErrorModal
        title={t('convert-and-edit:upload-error-modal.title')}
        text={t('convert-and-edit:upload-error-modal.text')}
        show={isUploadError}
        onHide={handleUploadErrorModalHide}
      />
    );
  }

  return (
    <>
      {store.isReady ? (
        <PdfEditor
          disabled={uploading}
          onSave={handleSave}
          zoom={zoom}
        />
      ) : (
        <LargeLoader />
      )}
      {errorModal}
    </>
  );
};

const Step3 = () => {
  const { result } = useEditorStore();
  const { t } = useTranslation(['common', 'convert-and-edit', 'edit-pdf']);
  const size = useMemo(() => filesize(result.fileSize), [result.fileSize]);

  const handleDownloadClick = useCallback((event) => {
    event.preventDefault();
    saveAs(event.currentTarget.href, event.currentTarget.download);

    sendEvent({
      category: 'file-table',
      action: 'download-click',
      label: 'pdf',
    });
  }, []);

  return (
    <>
      <Intro
        className="mb-xs-3"
        title={t('convert-and-edit:editing.ready-title')}
        titleClassName="h2"
      />
      <Container className="result-pdf-container__main">
        <div className="result-pdf-container">
          <div className="result-pdf-info">
            <div className="result-pdf-info-title">
              <FileIcon type="pdf"/>
              <span>{result.fileName}</span>
            </div>
            <span>({size})</span>
          </div>
          <a className="result-pdf-link" href={result.fileUrl} onClick={handleDownloadClick} download="pdf"
             target="_blank" rel="noreferrer">
            <IconDownload className="result-pdf-link-icon"/>
            <span>{t('convert-and-edit:file-table.download')}</span>
          </a>
        </div>
        <ActionsGrid className="result-pdf-actions-grid"
        title={t('common:cards.title')}
        edit
        rotate
        unlock
        merge
        split
        pdf2word
        pdf2excel
        pdf2jpg
        pdf2png
        pdf2ppt
        />
      </Container>
      <div className="result-link-container">
        <a className="link-button" href="/">
          <span>{t('convert-and-edit:delete-file')}</span>
          <IconDelete className="result-link-icon"/>
        </a>
      </div>
      <div className="grid_background">
        <GridEditSign/>
        <GridConvertPdf/>
        <GridConvertToPdf/>
        <GridEditPdf/>
      </div>
    </>
  );
};

Step1.propTypes = {
  onNextStep: PropTypes.func.isRequired,
};

export default ModalEditPdf;
